import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, MoreHorizontal, LogIn, Zap } from 'lucide-react';
import { BrokerAccount, CreateBrokerAccount, UpdateBrokerAccount } from '../../types';
import brokerService from '../../services/brokerService';
import AddBrokerAccountForm from './AddBrokerAccountForm';
import EditBrokerAccountForm from './EditBrokerAccountForm';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import ConfirmDialog from '../common/ConfirmDialog';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { useToast } from '../common/ToastContainer';
import { useOAuth } from '../../context/OAuthContext';
import { supportsOAuth as brokerSupportsOAuth, getPopupDimensions } from '../../config/brokerConfigs';
import { isWhiteLabel } from '../../config/tenantConfig';

const TH = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300';
const TH_R = `${TH} text-right`;

function formatIST(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '—';
  }
}

const statusVariant: Record<string, 'success' | 'danger' | 'warning' | 'default'> = {
  enabled: 'success',
  disabled: 'danger',
  pending: 'warning',
};

function LoginStatusBadge({ account }: { account: BrokerAccount }) {
  if (!brokerSupportsOAuth(account.broker_name)) return <span className="text-gray-400">—</span>;
  if (account.has_oauth_token && account.is_oauth_valid) {
    return <Badge variant="success" size="sm">Active</Badge>;
  }
  if (account.has_oauth_token && !account.is_oauth_valid) {
    return <Badge variant="warning" size="sm">Expired</Badge>;
  }
  return <Badge variant="default" size="sm">Not Connected</Badge>;
}

// --- Row action dropdown ---

function RowActions({ account, onEdit, onDelete }: {
  account: BrokerAccount;
  onEdit: (a: BrokerAccount) => void;
  onDelete: (clientId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="More options"
      >
        <MoreHorizontal className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 w-36 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 z-20">
          <button
            onClick={() => { onEdit(account); setOpen(false); }}
            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 first:rounded-t-lg"
          >
            <Edit className="w-3.5 h-3.5 mr-2" /> Edit
          </button>
          <button
            onClick={() => { setShowDeleteDialog(true); setOpen(false); }}
            className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 last:rounded-b-lg"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
          </button>
        </div>
      )}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => onDelete(account.client_id)}
        title="Delete Broker Account"
        message={`Are you sure you want to delete broker account "${account.client_id}"? This action cannot be undone.`}
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
        icon={<Trash2 className="h-6 w-6 text-red-600" />}
      />
    </div>
  );
}

// --- Mobile card ---

function MobileBrokerCard({ account, onEdit, onDelete, onLogin, isLoggingIn, onTest, isTestingConnection }: {
  account: BrokerAccount;
  onEdit: (a: BrokerAccount) => void;
  onDelete: (clientId: string) => void;
  onLogin: (a: BrokerAccount) => void;
  isLoggingIn: boolean;
  onTest: (clientId: string) => void;
  isTestingConnection: boolean;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{account.broker_name}</span>
          <Badge variant={statusVariant[account.account_status] || 'default'} size="sm" className="capitalize">
            {account.account_status}
          </Badge>
        </div>
        <RowActions account={account} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex justify-between">
          <span>Client ID</span>
          <span className="text-gray-900 dark:text-white font-medium">{account.client_id}</span>
        </div>
        <div className="flex justify-between">
          <span>Capital</span>
          <span className="text-gray-900 dark:text-white font-medium tabular-nums">₹{account.capital?.toLocaleString('en-IN') || '0'}</span>
        </div>
        <div className="flex justify-between items-center">
          <span>Login Status</span>
          <LoginStatusBadge account={account} />
        </div>
        {account.last_oauth_login && (
          <div className="flex justify-between">
            <span>Login Time</span>
            <span className="text-gray-900 dark:text-white">{formatIST(account.last_oauth_login)}</span>
          </div>
        )}
        {account.token_expires_at && (
          <div className="flex justify-between">
            <span>Expires At</span>
            <span className="text-gray-900 dark:text-white">{formatIST(account.token_expires_at)}</span>
          </div>
        )}
        {(account.broker_name === 'zerodha' || account.broker_name === 'zebu') && (
          <div className="flex justify-between items-center">
            <span>Auto-Login</span>
            <Badge variant={account.auto_login_enabled ? 'success' : 'default'} size="sm">
              {account.auto_login_enabled ? 'ON' : 'OFF'}
            </Badge>
          </div>
        )}
      </div>
      {brokerSupportsOAuth(account.broker_name) && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" className="flex-1" onClick={() => onLogin(account)} isLoading={isLoggingIn}
            leftIcon={!isLoggingIn ? <LogIn className="w-3.5 h-3.5" /> : undefined}>
            {isLoggingIn ? 'Connecting...' : 'Login'}
          </Button>
          {account.has_oauth_token && account.is_oauth_valid && (
            <Button size="sm" variant="outline" onClick={() => onTest(account.client_id)} isLoading={isTestingConnection}
              leftIcon={!isTestingConnection ? <Zap className="w-3.5 h-3.5" /> : undefined}>
              Test
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Main component ---

const BrokerAccountsList: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { initiateAuth, refreshAuthStatus, isOperationPending, clearError } = useOAuth();
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BrokerAccount | null>(null);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [loggingInClientId, setLoggingInClientId] = useState<string | null>(null);
  const [testingClientId, setTestingClientId] = useState<string | null>(null);

  useEffect(() => { fetchBrokerAccounts(); }, []);

  useEffect(() => {
    const handleRefresh = () => fetchBrokerAccounts();
    const handleAdd = () => setAddFormOpen(true);
    window.addEventListener('brokerRefresh', handleRefresh);
    window.addEventListener('brokerAdd', handleAdd);
    return () => {
      window.removeEventListener('brokerRefresh', handleRefresh);
      window.removeEventListener('brokerAdd', handleAdd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrokerAccounts = async () => {
    try {
      setIsLoading(true);
      const fetched = await brokerService.getBrokerAccounts();
      setAccounts(fetched);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch broker accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAccount = async (data: CreateBrokerAccount) => {
    try {
      setIsAddingAccount(true);
      setAddError(null);
      const newAccount = await brokerService.createBrokerAccount(data);
      setAccounts(prev => [...prev, newAccount]);
      showSuccess(`${data.broker_name} account added successfully!`);
      setAddFormOpen(false);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add broker account');
      throw err;
    } finally {
      setIsAddingAccount(false);
    }
  };

  const handleEditAccount = (account: BrokerAccount) => {
    setEditingAccount(account);
    setEditFormOpen(true);
    setEditError(null);
  };

  const handleUpdateAccount = async (clientId: string, updateData: UpdateBrokerAccount) => {
    try {
      setIsEditingAccount(true);
      setEditError(null);
      await brokerService.updateBrokerAccount(clientId, updateData);
      setAccounts(prev => prev.map(a => a.client_id === clientId ? { ...a, ...updateData, updated_at: new Date().toISOString() } : a));
      showSuccess('Broker account updated successfully!');
      setEditFormOpen(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update broker account');
      throw err;
    } finally {
      setIsEditingAccount(false);
    }
  };

  const handleDeleteAccount = async (clientId: string) => {
    try {
      await brokerService.deleteBrokerAccount(clientId);
      setAccounts(prev => prev.filter(a => a.client_id !== clientId));
      showSuccess('Broker account removed successfully!');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove broker account');
    }
  };

  const handleTestConnection = async (clientId: string) => {
    try {
      setTestingClientId(clientId);
      const response = await brokerService.testBrokerConnection(clientId);
      if (response.data?.status === 'connected') {
        showSuccess(response.data.details || 'Connection successful');
      } else {
        showError(response.data?.details || 'Connection test failed');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setTestingClientId(null);
    }
  };

  // --- OAuth login (inlined from OAuthButton) ---
  const handleOAuthLogin = async (account: BrokerAccount) => {
    const { broker_name: brokerName, client_id: clientId } = account;
    clearError(brokerName, clientId);
    setLoggingInClientId(clientId);

    // Clear stale localStorage state
    localStorage.removeItem('oauth_client_id');
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_broker');

    try {
      const loginResponse = await initiateAuth({
        brokerName,
        clientId,
        callbackUrl: `${window.location.origin}/oauth/callback`,
      });

      if (!loginResponse.success || !loginResponse.oauthUrl) {
        throw new Error(loginResponse.error || 'No OAuth URL received');
      }

      // On mobile, redirect instead of popup (popups are unreliable on mobile browsers)
      const isMobile = window.innerWidth < 640;
      if (isMobile) {
        window.location.href = loginResponse.oauthUrl;
        return;
      }

      const dims = getPopupDimensions(brokerName);
      const left = Math.round((window.screen.width - dims.width) / 2);
      const top = Math.round((window.screen.height - dims.height) / 2);
      const popup = window.open(
        loginResponse.oauthUrl,
        `${brokerName}-oauth`,
        `width=${dims.width},height=${dims.height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data.type === `${brokerName.toUpperCase()}_OAUTH_SUCCESS`) {
          refreshAuthStatus(brokerName, clientId);
          fetchBrokerAccounts();
          setLoggingInClientId(null);
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === `${brokerName.toUpperCase()}_OAUTH_ERROR`) {
          showError(event.data.error || 'OAuth authentication failed');
          setLoggingInClientId(null);
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setLoggingInClientId(null);
        }
      }, 1000);

      setTimeout(() => {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        if (!popup.closed) popup.close();
        if (isOperationPending(brokerName, clientId)) {
          showError('OAuth login timed out. Please try again.');
        }
        setLoggingInClientId(null);
      }, 600000);

    } catch (err: any) {
      showError(err.message || 'Failed to initiate OAuth login');
      setLoggingInClientId(null);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading broker accounts..." />;

  if (error) {
    return (
      <div>
        <ErrorAlert message={error} onClose={() => setError(null)} />
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={fetchBrokerAccounts}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {accounts.length === 0 ? (
        <div className={`${isWhiteLabel() ? 'bg-zebu-50 dark:bg-zebu-900/20 border-zebu-200 dark:border-zebu-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'} border rounded-lg p-6`}>
          <h3 className={`text-lg font-semibold ${isWhiteLabel() ? 'text-zebu-800 dark:text-zebu-200' : 'text-blue-800 dark:text-blue-200'} mb-2`}>No broker accounts connected</h3>
          <p className={`${isWhiteLabel() ? 'text-zebu-700 dark:text-zebu-300' : 'text-blue-700 dark:text-blue-300'} mb-4`}>
            Connect your trading account to start executing algorithmic strategies.
          </p>
          <Button onClick={() => setAddFormOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
            Add Your First Broker Account
          </Button>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Broker Accounts ({accounts.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="hidden sm:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className={TH}>Broker</th>
                    <th className={TH}>Client ID</th>
                    <th className={TH_R}>Capital</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Login Status</th>
                    <th className={TH}>Login Time</th>
                    <th className={TH}>Expires At</th>
                    <th className={TH}>Auto-Login</th>
                    <th className={TH_R}>Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {accounts.map(account => {
                    const isLoggingIn = loggingInClientId === account.client_id;
                    const hasOAuth = brokerSupportsOAuth(account.broker_name);
                    const showAutoLogin = account.broker_name === 'zerodha' || account.broker_name === 'zebu';
                    return (
                      <tr key={account.client_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white capitalize">
                          {account.broker_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                          {account.client_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white tabular-nums">
                          ₹{account.capital?.toLocaleString('en-IN') || '0'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Badge variant={statusVariant[account.account_status] || 'default'} size="sm" className="capitalize">
                            {account.account_status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <LoginStatusBadge account={account} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          {formatIST(account.last_oauth_login)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          {formatIST(account.token_expires_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {showAutoLogin ? (
                            <Badge variant={account.auto_login_enabled ? 'success' : 'default'} size="sm">
                              {account.auto_login_enabled ? 'ON' : 'OFF'}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <div className="flex items-center justify-end gap-2">
                            {hasOAuth && (
                              <Button size="sm" onClick={() => handleOAuthLogin(account)} isLoading={isLoggingIn}
                                leftIcon={!isLoggingIn ? <LogIn className="w-3.5 h-3.5" /> : undefined}>
                                {isLoggingIn ? 'Connecting...' : 'Login'}
                              </Button>
                            )}
                            {hasOAuth && account.has_oauth_token && account.is_oauth_valid && (
                              <Button size="sm" variant="outline" onClick={() => handleTestConnection(account.client_id)}
                                isLoading={testingClientId === account.client_id}
                                leftIcon={testingClientId !== account.client_id ? <Zap className="w-3.5 h-3.5" /> : undefined}>
                                Test
                              </Button>
                            )}
                            <RowActions account={account} onEdit={handleEditAccount} onDelete={handleDeleteAccount} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {accounts.map(account => (
                <MobileBrokerCard
                  key={account.client_id}
                  account={account}
                  onEdit={handleEditAccount}
                  onDelete={handleDeleteAccount}
                  onLogin={handleOAuthLogin}
                  isLoggingIn={loggingInClientId === account.client_id}
                  onTest={handleTestConnection}
                  isTestingConnection={testingClientId === account.client_id}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AddBrokerAccountForm
        open={addFormOpen}
        onClose={() => setAddFormOpen(false)}
        onSubmit={handleAddAccount}
        isLoading={isAddingAccount}
        error={addError}
        onClearError={() => setAddError(null)}
      />

      <EditBrokerAccountForm
        open={editFormOpen}
        account={editingAccount}
        onClose={() => { setEditFormOpen(false); setEditingAccount(null); }}
        onSubmit={handleUpdateAccount}
        isLoading={isEditingAccount}
        error={editError}
        onClearError={() => setEditError(null)}
      />
    </div>
  );
};

export default BrokerAccountsList;
